import { Box, Stack, Skeleton, useTheme, Grid } from "@mui/material";
import React from "react";

/**
 * Skeleton loading component for PaymentDialog
 *
 * Displays a comprehensive skeleton UI that matches the actual payment dialog layout
 * while data is being loaded. This was extracted from the inline skeleton code in
 * PaymentDialog.tsx to improve code organization and reusability.
 *
 * Features:
 * - Left panel with payee, payment, and document sections
 * - Right panel with associations, reconciliation, and comments sections
 * - Matches the actual dialog dimensions and spacing
 * - Uses proper skeleton variants for different UI elements
 */
export const PaymentDialogSkeleton: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        minWidth: "1200px",
        width: "100%",
      }}
    >
      {/* Left Panel Skeleton */}
      <Box sx={{ flex: 0.4, minWidth: "480px" }}>
        <Stack spacing={3}>
          {/* Payee Section Skeleton */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Skeleton variant="text" width={80} height={32} />
              <Skeleton variant="circular" width={24} height={24} />
            </Box>
            <Grid container spacing={2}>
              {/* First row: Two fields */}
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              {/* Full width fields */}
              <Grid size={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              {/* City, State, Zip row */}
              <Grid size={5}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={2}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={5}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            </Grid>
          </Box>

          {/* Payment Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={100} height={32} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {/* First row: Two fields */}
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              {/* Second row: Two fields */}
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              {/* Full width field */}
              <Grid size={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            </Grid>
          </Box>

          {/* Invoices & Attachments Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid size={12}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid size={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Box>

      {/* Right Panel Skeleton with Divider */}
      <Box
        sx={{
          borderLeft: `2px solid ${theme.palette.divider}`,
          pl: 3,
          flex: 0.6,
          minWidth: "720px",
        }}
      >
        <Stack spacing={3}>
          {/* Associated Organization Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={200} height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={24} />
          </Box>

          {/* Associated Reports Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={160} height={28} sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            </Stack>
          </Box>

          {/* Associated Facilities Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={170} height={28} sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            </Stack>
          </Box>

          {/* Reconciliation Status Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={180} height={28} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="70%" height={16} />
            </Box>
            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
          </Box>

          {/* Comments Section Skeleton */}
          <Box>
            <Skeleton variant="text" width={100} height={28} sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box
                sx={{
                  borderLeft: "3px solid #1976d2",
                  pl: 2,
                  minHeight: 40,
                }}
              >
                <Skeleton variant="text" width="95%" height={16} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="40%" height={14} />
              </Box>
              <Box
                sx={{
                  borderLeft: "3px solid #9c27b0",
                  pl: 2,
                  minHeight: 40,
                }}
              >
                <Skeleton variant="text" width="90%" height={16} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="70%" height={16} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="35%" height={14} />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};
